import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { isEmpty, map, pick } from 'lodash'
import PropTypes from 'prop-types'

import { PUBLIC_URL } from '../../common/constants'
import { TireMeshIcon } from '../../icons'

const MESH_COORDS = [
  {
    axis: 'front',
    wheels: [
      { left: 0, top: 15 },
      { right: 0, top: 15 },
    ],
  },
  {
    axis: 'rear',
    wheels: [
      { left: 0, top: 210 },
      { right: 0, top: 210 },
    ],
  },
]

const TireMeshImg = styled('img')({
  width: '200px',
  height: 'auto',
})

const TireMeshPointer = styled('span')({
  position: 'absolute',
  zIndex: '100',
})

const TireMeshContainer = styled(Box)({
  position: 'relative',
  display: 'inline-flex',
  margin: 0,
  padding: 0,
  width: '200px',
  height: 'auto',
})

const TireIcon = styled(TireMeshIcon)(({ color }) => ({
  backgroundColor: color,
  borderRadius: '0.8rem',
}))

function TyresConditionList() {
  const list = [
    { title: 'Nowe' },
    { title: '25% zużycia' },
    { title: '50% zużycia' },
    { title: '75% zużycia' },
    { title: 'Do wymiany' },
  ]
  return (
    <List
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        pl: 2,
        height: 300,
        '&:before': {
          position: 'absolute',
          top: 8,
          left: 0,
          bottom: 8,
          display: 'block',
          content: '" "',
          height: 'calc(100% - 16px)',
          width: 12,
          background:
            'linear-gradient(180deg, #19BF0A 0%, #F1F512 34.76%, #F85C04 66.05%, #DA0000 100%)',
        },
      }}
    >
      {map(list, ({ title, subtitle }, key) => (
        <ListItem
          key={key}
          disablePadding
          sx={{
            padding: '0 !important',
            '&:before': { display: 'none !important' },
          }}
        >
          <ListItemText
            primary={title}
            primaryTypographyProps={{ variant: 'caption' }}
            secondary={subtitle}
            secondaryTypographyProps={{ variant: 'caption' }}
          />
        </ListItem>
      ))}
    </List>
  )
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.root}`]: { border: 0, padding: theme.spacing(1) },
  [`&.${tableCellClasses.root}:first-of-type`]: {
    color: theme.palette.gray.dark,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.background.default,
  },
}))

function TyresCondition({ digitalCertificate }) {
  const { tyreConditions } = digitalCertificate
  const { t } = useTranslation()
  return map(tyreConditions, (tyreSet, key) => (
    <Box key={key} sx={{ mb: 2 }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        {t(`tyresCondition.${tyreSet.type}`)}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box
              sx={{
                minWidth: 100,
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
              }}
            >
              <Typography variant="h4">{t('controlsEditTires')}</Typography>
              <TyresConditionList />
            </Box>
            {!isEmpty(tyreSet) && (
              <TireMeshContainer>
                {map(MESH_COORDS, (axis) =>
                  map(axis.wheels, (wheel, tskey) => (
                    <TireMeshPointer key={tskey} sx={wheel}>
                      {tyreSet[axis.axis].conditionId === 1 && (
                        <TireIcon color="#30c50b" />
                      )}
                      {tyreSet[axis.axis].conditionId === 5 && (
                        <TireIcon color="#c0e910" />
                      )}
                      {tyreSet[axis.axis].conditionId === 2 && (
                        <TireIcon color="#f4a90b" />
                      )}
                      {tyreSet[axis.axis].conditionId === 3 && (
                        <TireIcon color="#f14703" />
                      )}
                      {tyreSet[axis.axis].conditionId === 4 && (
                        <TireIcon color="#db0400" />
                      )}
                    </TireMeshPointer>
                  )),
                )}
                <TireMeshImg
                  src={`${PUBLIC_URL}/assets/images/tiremesh2.png`}
                />
              </TireMeshContainer>
            )}
          </Box>
        </Grid>
        <Grid item xs={6}>
          {map(pick(tyreSet, ['front', 'rear']), (axis, tkey) => (
            <Table key={tkey} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={({ palette }) => ({
                      backgroundColor: palette.gray.mediumDark,
                      border: 0,
                      p: 1,
                    })}
                  >
                    {t(`tyres.axis.${tkey}`)}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <StyledTableRow>
                  <StyledTableCell>{t('tyres.props.make')}</StyledTableCell>
                  <StyledTableCell>{axis.make}</StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>{t('tyres.props.model')}</StyledTableCell>
                  <StyledTableCell>{axis.model}</StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>{t('tyres.props.size')}</StyledTableCell>
                  <StyledTableCell>{axis.size}</StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>{t('tyres.props.age')}</StyledTableCell>
                  <StyledTableCell>{axis.age}</StyledTableCell>
                </StyledTableRow>
              </TableBody>
            </Table>
          ))}
        </Grid>
      </Grid>
    </Box>
  ))
}

TyresCondition.propTypes = {
  digitalCertificate: PropTypes.object,
}

TyresCondition.defaultProps = {
  digitalCertificate: {},
}

export default TyresCondition
